import moment from 'moment-timezone';
import { FilterValue, IdType } from 'react-table';

import sessionState from 'utils/session-storage';
import { FilterMetadata, RowData, TableSessionData } from 'views/MTable/Table.model';
import { TimeFormat } from 'utils/enums/time-format';
import useFlaggedCallback from './use-flagged-callback';

type ReturnFunc = <TableFields extends RowData>(dataToCache: TableSessionData<TableFields>) => void;
type Options = {
  persistentKey?: string | null;
  excludedFilterIds?: string[];
  persistSearch?: boolean;
};

const useTableCache = <TableFields extends RowData>(key: string | null, options?: Options): ReturnFunc => {
  return useFlaggedCallback(
    (dataToCache: TableSessionData<TableFields>) => {
      if (!key) {
        return;
      }

      const excludedFilterIds = options?.excludedFilterIds || [];

      const formattedSessionFilters = dataToCache.filters.reduce(
        (
          acc: { id: IdType<TableFields>; value: FilterValue }[],
          filter: { id: IdType<TableFields>; value: FilterValue; metadata?: FilterMetadata },
        ) => {
          if (acc.find((accFilter) => accFilter.id === filter.id)) {
            return acc;
          }

          if (Array.isArray(filter.value)) {
            return [
              ...acc,
              {
                id: filter.id,
                value: filter.value.map((value: FilterValue) =>
                  moment.isMoment(value) ? value.format(TimeFormat.ServerFormatDateTime) : value,
                ),
                metadata: filter.metadata,
              },
            ];
          }

          return [
            ...acc,
            {
              id: filter.id,
              value: moment.isMoment(filter.value) ? filter.value.format(TimeFormat.ServerFormatDateTime) : filter.value,
              metadata: filter.metadata,
            },
          ];
        },
        [],
      );

      const formattedPersistentFilters = formattedSessionFilters.filter(
        (filter: { id: IdType<TableFields>; value: FilterValue }) => !excludedFilterIds.includes(String(filter.id)),
      );

      const sessionStateToCache = {
        ...(dataToCache.search ? { search: dataToCache.search } : {}),
        sort: dataToCache.sort,
        page: dataToCache.page,
        limit: dataToCache.limit,
        filters: formattedSessionFilters,
        hiddenColumns: dataToCache.hiddenColumns,
      };

      sessionState.save(key, sessionStateToCache);

      if (options?.persistentKey) {
        const persistentStateToCache = {
          ...(options?.persistSearch !== false && dataToCache.search ? { search: dataToCache.search } : {}),
          sort: dataToCache.sort,
          page: dataToCache.page,
          limit: dataToCache.limit,
          filters: formattedPersistentFilters,
          hiddenColumns: dataToCache.hiddenColumns,
        };

        localStorage.setItem(options.persistentKey, JSON.stringify(persistentStateToCache));
      }
    },
    [key, options?.persistentKey, options?.persistSearch, JSON.stringify(options?.excludedFilterIds || [])],
  );
};

export default useTableCache;
